import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Layout from '@/components/Layout';
import { 
  Target, 
  Database, 
  Zap, 
  RefreshCw,
  Plus,
  Edit3,
  Save,
  X,
  Search,
  Filter,
  Star,
  TrendingUp,
  TrendingDown,
  Activity,
  CheckCircle,
  Clock,
  AlertTriangle,
  Eye,
  Settings,
  Users,
  Building,
  PieChart,
  BarChart3
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Recommendations = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('repository');
  const [recommendations, setRecommendations] = useState([]);
  const [triggers, setTriggers] = useState([]);
  const [productClasses, setProductClasses] = useState([]);
  const [dataFeeds, setDataFeeds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProductClass, setSelectedProductClass] = useState('all');
  const [editingRecommendation, setEditingRecommendation] = useState(null);
  const [newRecommendation, setNewRecommendation] = useState({
    isin: '',
    symbol: '',
    name: '',
    productClass: '',
    recommendation: 'BUY',
    targetPrice: '',
    currentPrice: '',
    rationale: '',
    productLead: ''
  });

  useEffect(() => {
    fetchRecommendations();
    fetchTriggers();
    fetchProductClasses();
    fetchDataFeeds();
  }, []);

  const fetchRecommendations = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const response = await axios.get(`${API}/admin/ps-recommendations`, config);
      setRecommendations(response.data);
    } catch (error) {
      console.error('Error fetching recommendations:', error);
      toast.error('Failed to fetch recommendations');
    } finally {
      setLoading(false);
    }
  };

  const fetchTriggers = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const response = await axios.get(`${API}/admin/trigger-conditions`, config);
      setTriggers(response.data);
    } catch (error) {
      console.error('Error fetching triggers:', error);
    }
  };

  const fetchProductClasses = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const response = await axios.get(`${API}/admin/product-classes`, config);
      setProductClasses(response.data);
    } catch (error) {
      console.error('Error fetching product classes:', error);
    }
  };

  const fetchDataFeeds = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const response = await axios.get(`${API}/admin/data-feeds`, config);
      setDataFeeds(response.data);
    } catch (error) {
      console.error('Error fetching data feeds:', error);
    }
  };

  const addRecommendation = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.post(`${API}/admin/ps-recommendations`, newRecommendation, config);
      
      toast.success('Recommendation added successfully');
      setNewRecommendation({
        isin: '',
        symbol: '',
        name: '',
        productClass: '',
        recommendation: 'BUY',
        targetPrice: '',
        currentPrice: '',
        rationale: '',
        productLead: ''
      });
      fetchRecommendations();
    } catch (error) {
      console.error('Error adding recommendation:', error);
      toast.error('Failed to add recommendation');
    }
  };

  const updateRecommendation = async (id, updates) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.put(`${API}/admin/ps-recommendations/${id}`, updates, config);
      
      toast.success('Recommendation updated successfully');
      setEditingRecommendation(null);
      fetchRecommendations();
    } catch (error) {
      console.error('Error updating recommendation:', error);
      toast.error('Failed to update recommendation');
    }
  };

  const refreshDataFeeds = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.post(`${API}/admin/refresh-data-feeds`, {}, config);
      
      toast.success('Data feeds refreshed successfully');
      fetchRecommendations();
      fetchDataFeeds();
    } catch (error) {
      console.error('Error refreshing data feeds:', error);
      toast.error('Failed to refresh data feeds');
    }
  };

  const createTrigger = async (triggerData) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.post(`${API}/admin/trigger-conditions`, triggerData, config);
      
      toast.success('Trigger condition created successfully');
      fetchTriggers();
    } catch (error) {
      console.error('Error creating trigger:', error);
      toast.error('Failed to create trigger condition');
    }
  };

  const filteredRecommendations = recommendations.filter(rec => {
    const matchesSearch = rec.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         rec.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         rec.isin.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesClass = selectedProductClass === 'all' || rec.productClass === selectedProductClass;
    return matchesSearch && matchesClass;
  });

  const ProductRepository = () => (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Recommendations</p>
                <p className="text-2xl font-bold text-green-600">{recommendations.length}</p>
              </div>
              <Target className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Buy Recommendations</p>
                <p className="text-2xl font-bold text-ugmonk-dark">
                  {recommendations.filter(r => r.recommendation === 'BUY').length}
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-ugmonk-grey" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Product Classes</p>
                <p className="text-2xl font-bold text-ugmonk-dark">{productClasses.length}</p>
              </div>
              <Building className="h-8 w-8 text-ugmonk-grey" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Data Feeds Active</p>
                <p className="text-2xl font-bold text-purple-600">
                  {dataFeeds.filter(d => d.status === 'active').length}
                </p>
              </div>
              <Activity className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by symbol, name, or ISIN..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <select
              value={selectedProductClass}
              onChange={(e) => setSelectedProductClass(e.target.value)}
              className="px-3 py-2 border rounded-lg"
            >
              <option value="all">All Product Classes</option>
              {productClasses.map(pc => (
                <option key={pc.id} value={pc.name}>{pc.name}</option>
              ))}
            </select>
            <Button onClick={refreshDataFeeds} className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4" />
              Refresh Data
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Add New Recommendation */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Add New Recommendation
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              placeholder="ISIN"
              value={newRecommendation.isin}
              onChange={(e) => setNewRecommendation(prev => ({ ...prev, isin: e.target.value }))}
            />
            <Input
              placeholder="Symbol"
              value={newRecommendation.symbol}
              onChange={(e) => setNewRecommendation(prev => ({ ...prev, symbol: e.target.value }))}
            />
            <Input
              placeholder="Security Name"
              value={newRecommendation.name}
              onChange={(e) => setNewRecommendation(prev => ({ ...prev, name: e.target.value }))}
            />
            <select
              value={newRecommendation.productClass}
              onChange={(e) => setNewRecommendation(prev => ({ ...prev, productClass: e.target.value }))}
              className="px-3 py-2 border rounded-lg"
            >
              <option value="">Select Product Class</option>
              {productClasses.map(pc => (
                <option key={pc.id} value={pc.name}>{pc.name}</option>
              ))}
            </select>
            <select
              value={newRecommendation.recommendation}
              onChange={(e) => setNewRecommendation(prev => ({ ...prev, recommendation: e.target.value }))}
              className="px-3 py-2 border rounded-lg"
            >
              <option value="BUY">BUY</option>
              <option value="HOLD">HOLD</option>
              <option value="SELL">SELL</option>
            </select>
            <Input
              placeholder="Target Price"
              type="number"
              value={newRecommendation.targetPrice}
              onChange={(e) => setNewRecommendation(prev => ({ ...prev, targetPrice: e.target.value }))}
            />
          </div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              placeholder="Product Lead"
              value={newRecommendation.productLead}
              onChange={(e) => setNewRecommendation(prev => ({ ...prev, productLead: e.target.value }))}
            />
            <Input
              placeholder="Rationale"
              value={newRecommendation.rationale}
              onChange={(e) => setNewRecommendation(prev => ({ ...prev, rationale: e.target.value }))}
            />
          </div>
          <div className="mt-4">
            <Button onClick={addRecommendation} className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Add Recommendation
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Recommendations List */}
      <Card>
        <CardHeader>
          <CardTitle>P&S Recommendations Repository</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left p-3 font-medium">ISIN / Symbol</th>
                  <th className="text-left p-3 font-medium">Security Name</th>
                  <th className="text-left p-3 font-medium">Product Class</th>
                  <th className="text-left p-3 font-medium">Recommendation</th>
                  <th className="text-left p-3 font-medium">Price</th>
                  <th className="text-left p-3 font-medium">Product Lead</th>
                  <th className="text-left p-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecommendations.map((rec) => (
                  <tr key={rec.id} className="border-b hover:bg-gray-50">
                    <td className="p-3">
                      <div>
                        <div className="font-medium">{rec.symbol}</div>
                        <div className="text-sm text-gray-500">{rec.isin}</div>
                      </div>
                    </td>
                    <td className="p-3">{rec.name}</td>
                    <td className="p-3">
                      <span className="inline-block bg-[#fffdf0] text-ugmonk-darker text-xs px-2 py-1 rounded">
                        {rec.productClass}
                      </span>
                    </td>
                    <td className="p-3">
                      <span className={`inline-block text-xs px-2 py-1 rounded font-medium ${
                        rec.recommendation === 'BUY' ? 'bg-green-100 text-green-800' :
                        rec.recommendation === 'HOLD' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {rec.recommendation}
                      </span>
                    </td>
                    <td className="p-3">
                      <div>
                        <div className="font-medium">₹{rec.currentPrice}</div>
                        <div className="text-sm text-gray-500">Target: ₹{rec.targetPrice}</div>
                      </div>
                    </td>
                    <td className="p-3">{rec.productLead}</td>
                    <td className="p-3">
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline">
                          <Edit3 className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const TriggerConditions = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            Trigger Conditions Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {triggers.map((trigger) => (
              <div key={trigger.id} className="border rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-semibold">{trigger.name}</span>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        trigger.status === 'active' ? 'bg-green-100 text-green-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {trigger.status}
                      </span>
                    </div>
                    
                    <p className="text-gray-700 mb-3">{trigger.description}</p>
                    
                    <div className="bg-[#fffdf0] p-3 rounded-lg mb-3">
                      <p className="text-sm font-medium text-ugmonk-darker">Conditions:</p>
                      <ul className="text-sm text-ugmonk-dark mt-1 list-disc list-inside">
                        {trigger.conditions.map((condition, index) => (
                          <li key={index}>{condition}</li>
                        ))}
                      </ul>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Triggered:</span>
                        <span className="ml-1 font-medium">{trigger.triggerCount} times</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Last Triggered:</span>
                        <span className="ml-1 font-medium">
                          {trigger.lastTriggered ? new Date(trigger.lastTriggered).toLocaleDateString() : 'Never'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Product Lead:</span>
                        <span className="ml-1 font-medium">{trigger.productLead}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Action:</span>
                        <span className="ml-1 font-medium">{trigger.action}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 ml-4">
                    <Button size="sm" variant="outline">
                      <Edit3 className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button size="sm" variant="outline">
                      <Settings className="h-4 w-4 mr-1" />
                      Configure
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Add New Trigger */}
      <Card>
        <CardHeader>
          <CardTitle>Create New Trigger Condition</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input placeholder="Trigger Name" />
              <select className="px-3 py-2 border rounded-lg">
                <option value="">Select Product Lead</option>
                <option value="equity_lead">Equity Product Lead</option>
                <option value="mf_lead">Mutual Fund Lead</option>
                <option value="aif_lead">AIF Product Lead</option>
              </select>
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-medium">Condition Examples:</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div className="flex items-center gap-2">
                  <input type="checkbox" />
                  <span className="text-sm">Asset is >X% of portfolio</span>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" />
                  <span className="text-sm">Client category is APEX</span>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" />
                  <span className="text-sm">Security not in repository</span>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" />
                  <span className="text-sm">Price deviation >15%</span>
                </div>
              </div>
            </div>

            <Button className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Create Trigger
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const DataEnrichment = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Repository Data Enrichment
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Daily Price Feeds</h3>
              <div className="space-y-3">
                {dataFeeds.filter(d => d.type === 'price').map((feed) => (
                  <div key={feed.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <div className="font-medium">{feed.name}</div>
                      <div className="text-sm text-gray-500">
                        Last Updated: {new Date(feed.lastUpdate).toLocaleString()}
                      </div>
                    </div>
                    <div className={`w-3 h-3 rounded-full ${
                      feed.status === 'active' ? 'bg-green-500' : 'bg-red-500'
                    }`}></div>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Fund Performance</h3>
              <div className="space-y-3">
                {dataFeeds.filter(d => d.type === 'performance').map((feed) => (
                  <div key={feed.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <div className="font-medium">{feed.name}</div>
                      <div className="text-sm text-gray-500">
                        Last Updated: {new Date(feed.lastUpdate).toLocaleString()}
                      </div>
                    </div>
                    <div className={`w-3 h-3 rounded-full ${
                      feed.status === 'active' ? 'bg-green-500' : 'bg-red-500'
                    }`}></div>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Data Subscriptions</h3>
              <div className="space-y-3">
                {dataFeeds.filter(d => d.type === 'subscription').map((feed) => (
                  <div key={feed.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <div className="font-medium">{feed.name}</div>
                      <div className="text-sm text-gray-500">
                        Provider: {feed.provider}
                      </div>
                    </div>
                    <div className={`w-3 h-3 rounded-full ${
                      feed.status === 'active' ? 'bg-green-500' : 'bg-red-500'
                    }`}></div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-semibold">Automated Data Enrichment</h4>
                <p className="text-sm text-gray-600">
                  Last enrichment run: {new Date().toLocaleString()}
                </p>
              </div>
              <Button onClick={refreshDataFeeds} className="flex items-center gap-2">
                <RefreshCw className="h-4 w-4" />
                Run Enrichment
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Enrichment Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Securities Updated</p>
                <p className="text-2xl font-bold text-ugmonk-dark">1,247</p>
              </div>
              <BarChart3 className="h-8 w-8 text-ugmonk-grey" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Price Accuracy</p>
                <p className="text-2xl font-bold text-green-600">99.8%</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Failed Updates</p>
                <p className="text-2xl font-bold text-ugmonk-dark">3</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-ugmonk-grey" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Processing Time</p>
                <p className="text-2xl font-bold text-purple-600">2.4m</p>
              </div>
              <Clock className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const tabs = [
    { id: 'repository', label: 'Product Repository', icon: Database, component: ProductRepository },
    { id: 'triggers', label: 'Trigger Conditions', icon: Zap, component: TriggerConditions },
    { id: 'enrichment', label: 'Data Enrichment', icon: RefreshCw, component: DataEnrichment },
  ];

  const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component || ProductRepository;

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Target className="h-8 w-8 text-ugmonk-dark" />
            <h1 className="text-3xl font-bold text-gray-900">Central P&S Recommendations Repository</h1>
          </div>
          <p className="text-gray-600">
            Security Level Recommendations under different Product classes (Direct Equity / Mutual Funds/ AIFs etc)
          </p>
        </div>

        {/* Navigation Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === tab.id
                        ? 'border-ugmonk-yellow text-ugmonk-dark'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Active Tab Content */}
        <ActiveComponent />
      </div>
    </Layout>
  );
};

export default Recommendations;